import React, { useRef, useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { RoomQuotationTemplate } from '../utils/ordering_template';
import { Button } from '../../../components/ui/Button';
import { X, Loader2, Wallet, Landmark, CreditCard, Banknote, CheckCircle2, QrCode, Edit2 } from 'lucide-react';
import html2pdf from 'html2pdf.js';

// ─── Inline Payment Modal ────────────────────────────────────────────────────
// Reuses the same fields & design as advance_payment.jsx but scoped to
// "payment collection only" — no project selector, no follow-up tab.

const PAYMENT_METHODS = [
  { id: 'bank_transfer', label: 'Bank',   icon: <Landmark size={14} /> },
  { id: 'upi_qr',       label: 'UPI',    icon: <CreditCard size={14} /> },
  { id: 'cash',         label: 'Cash',   icon: <Banknote size={14} /> },
  { id: 'cheque',       label: 'Cheque', icon: <CheckCircle2 size={14} /> },
];

const InputField = ({ label, type = 'text', placeholder, value, onChange, readOnly }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      readOnly={readOnly}
      className={`w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-700 font-medium outline-none transition-all
        ${readOnly ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : 'bg-white focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue'}`}
    />
  </div>
);

const PaymentModal = ({ quotationNo, grandTotal, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    amount: grandTotal ? String(grandTotal) : '',
    method: 'bank_transfer',
    transactionId: '',
    bankName: '',
    branch: '',
    ifscCode: '',
    upiPhone: '',
    receiverName: '',
    receiverPhone: '',
    chequeNumber: '',
    chequeDate: '',
    remarks: '',
  });

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleConfirm = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }
    /* Developer : Nithin - Date : 21-07-2026 - Added 10-digit phone number and mandatory Receiver Name validations for UPI and Cash */
    if (form.method === 'upi_qr' && (!form.upiPhone || !/^\d{10}$/.test((form.upiPhone || "").trim()))) return alert("Please enter a valid 10-digit UPI Phone Number");
    if (form.method === 'cash' && (!form.receiverName || !form.receiverName.trim())) return alert("Please enter Receiver Name");
    if (form.method === 'cash' && (!form.receiverPhone || !/^\d{10}$/.test((form.receiverPhone || "").trim()))) return alert("Please enter a valid 10-digit Receiver Phone Number");
    setLoading(true);
    try {
      await onConfirm(form);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">

        {/* Header */}
        <div className="bg-slate-900 px-5 py-3 flex items-center justify-between">
          <div>
            <p className="text-[13px] font-bold text-white font-serif tracking-widest uppercase">Verve Kitchens</p>
            <p className="text-[9px] text-slate-400 font-semibold tracking-widest uppercase">Record Payment</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-3 max-h-[80vh] overflow-y-auto">

          {/* Banner */}
          <div className="p-2.5 bg-blue-50/60 rounded-xl border border-blue-100 flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-brand-blue shrink-0">
              <Wallet size={16} />
            </div>
            <div>
              <p className="text-[11px] font-bold text-blue-900 tracking-wider uppercase">Quotation Payment</p>
              <p className="text-[10px] font-medium text-brand-blue">{quotationNo || 'Record advance / full payment from client'}</p>
            </div>
          </div>

          {/* Date & Amount */}
          <div className="grid grid-cols-2 gap-2">
            <InputField
              label="Transaction Date *"
              type="date"
              value={form.paymentDate}
              onChange={e => setField('paymentDate', e.target.value)}
            />
            <InputField
              label="Amount Received (₹) *"
              type="number"
              placeholder="0.00"
              value={form.amount}
              onChange={e => setField('amount', e.target.value)}
            />
          </div>

          {/* Payment Method */}
          <div>
            <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-2 block">Payment Mode</label>
            <div className="grid grid-cols-4 gap-2">
              {PAYMENT_METHODS.map(m => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setField('method', m.id)}
                  className={`flex flex-col items-center justify-center gap-1 py-2 rounded-xl border-2 transition-all
                    ${form.method === m.id
                      ? 'bg-brand-blue/5 border-brand-blue text-brand-blue shadow-sm'
                      : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'}`}
                >
                  {m.icon}
                  <span className="text-[9px] font-bold uppercase tracking-wider">{m.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ── Bank Transfer ── */}
          {form.method === 'bank_transfer' && (
            <div className="grid grid-cols-2 gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
              <InputField label="Transaction / Ref ID" placeholder="TRX-123..." value={form.transactionId} onChange={e => setField('transactionId', e.target.value)} />
              <InputField label="Bank Name" placeholder="HDFC, SBI..." value={form.bankName} onChange={e => setField('bankName', e.target.value)} />
              <InputField label="Branch" placeholder="Branch Name" value={form.branch} onChange={e => setField('branch', e.target.value)} />
              <InputField label="IFSC Code" placeholder="IFSC Code" value={form.ifscCode} onChange={e => setField('ifscCode', e.target.value)} />
            </div>
          )}

          {/* ── UPI — shows QR placeholder ── */}
          {form.method === 'upi_qr' && (
            <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
              {/* QR placeholder box — user will add QR image here later */}
              <div className="flex flex-col items-center justify-center gap-2 py-5 border-2 border-dashed border-brand-blue/30 rounded-xl bg-blue-50/40">
                <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center text-brand-blue">
                  <QrCode size={32} />
                </div>
                <p className="text-[11px] font-bold text-brand-blue uppercase tracking-wider">Scan to Pay</p>
                <p className="text-[10px] text-slate-400">QR code will appear here once uploaded</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <InputField label="UPI UTR / Ref No." placeholder="TRX-123..." value={form.transactionId} onChange={e => setField('transactionId', e.target.value)} />
                <InputField label="UPI Phone Number *" placeholder="10-digit number" value={form.upiPhone} onChange={e => setField('upiPhone', e.target.value.replace(/\D/g, '').slice(0, 10))} />
              </div>
            </div>
          )}

          {/* ── Cash ── */}
          {form.method === 'cash' && (
            <div className="grid grid-cols-2 gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
              <InputField label="Receiver Name *" placeholder="Who received" value={form.receiverName} onChange={e => setField('receiverName', e.target.value)} />
              <InputField label="Receiver Phone *" placeholder="10-digit number" value={form.receiverPhone} onChange={e => setField('receiverPhone', e.target.value.replace(/\D/g, '').slice(0, 10))} />
            </div>
          )}

          {/* ── Cheque ── */}
          {form.method === 'cheque' && (
            <div className="grid grid-cols-2 gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
              <InputField label="Cheque Number" placeholder="6-digit No." value={form.chequeNumber} onChange={e => setField('chequeNumber', e.target.value)} />
              <InputField label="Cheque Date" type="date" value={form.chequeDate} onChange={e => setField('chequeDate', e.target.value)} />
              <InputField label="Issuing Bank" placeholder="Bank name" value={form.bankName} onChange={e => setField('bankName', e.target.value)} />
              <InputField label="IFSC Code" placeholder="IFSC Code" value={form.ifscCode} onChange={e => setField('ifscCode', e.target.value)} />
            </div>
          )}

          {/* Remarks */}
          <div>
            <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1 block">Internal Remarks</label>
            <input
              type="text"
              className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 font-medium focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue outline-none transition-all"
              placeholder="Optional notes..."
              value={form.remarks}
              onChange={e => setField('remarks', e.target.value)}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t border-slate-100">
            <button
              className="flex-1 py-2 border border-slate-200 rounded-lg text-[11px] text-slate-600 font-bold hover:bg-slate-50 transition-colors"
              onClick={onClose}
              disabled={loading}
            >
              Discard
            </button>
            <button
              className="flex-1 py-2 bg-slate-900 hover:bg-black text-white rounded-md text-[11px] font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-50"
              onClick={handleConfirm}
              disabled={loading || !form.amount || Number(form.amount) <= 0}
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <CheckCircle2 size={14} />
                  Confirm &amp; Submit
                </>
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

// ─── Main Preview Modal ───────────────────────────────────────────────────────

export const QuotationPreviewModal = ({ data, onClose, onSubmit }) => {
  const { user } = useAuthStore();
  const loggedInUserName = user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  const rawDesigner = (data?.referenceDesigner && data.referenceDesigner !== '-') ? data.referenceDesigner : ((data?.designer && data.designer !== '-') ? data.designer : (loggedInUserName || '-'));
  const pdfRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [sendToOwner] = useState(true);
  const [isConfirmOrder, setIsConfirmOrder] = useState(data?.isConfirmOrder ?? data?.isConformOrder ?? false);

  // Called when user clicks "Submit" — skip payment modal, payments collected separately
  const handleSubmitClick = () => {
    handlePaymentConfirm(null);
  };

  // Called when payment form is confirmed
  const handlePaymentConfirm = async (paymentData) => {
    if (!pdfRef.current || !onSubmit) return;
    try {
      setIsSubmitting(true);
      setShowPayment(false);

      const opt = {
        margin: 10,
        filename: `Ordering_${data.quotationNo || Date.now()}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      const pdfBlob = await html2pdf().from(pdfRef.current).set(opt).output('blob');

      // Pass both the pdf and the payment details along with isConfirmOrder to the parent
      await onSubmit(pdfBlob, sendToOwner && isConfirmOrder, paymentData, isConfirmOrder);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4">
        <div className="bg-white rounded-xl shadow-2xl flex flex-col max-h-[94vh] w-full max-w-5xl overflow-hidden relative">

          {/* Header Action Bar */}
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50 shrink-0">
            <h2 className="text-lg font-bold text-slate-800">Quotation Preview</h2>
            <div className="flex items-center gap-2.5">
              <label className="flex items-center gap-2 mr-1 bg-white px-3 py-1.5 rounded-lg border border-slate-300 shadow-sm cursor-pointer hover:bg-slate-50 transition-all">
                <input 
                  type="checkbox" 
                  checked={isConfirmOrder}
                  onChange={(e) => setIsConfirmOrder(e.target.checked)}
                  className="w-4 h-4 rounded text-brand-blue focus:ring-brand-blue cursor-pointer"
                />
                <span className="text-sm font-semibold text-slate-800 select-none">Confirm Order</span>
              </label>
              {!data?.isInitial && (
                <label className={`flex items-center gap-2 mr-2 px-3 py-1.5 rounded-lg border border-slate-200 transition-all ${!isConfirmOrder ? 'bg-slate-50 opacity-40 cursor-not-allowed' : 'bg-slate-100 opacity-80 cursor-not-allowed'}`}>
                  <input 
                    type="checkbox" 
                    checked={isConfirmOrder && sendToOwner}
                    readOnly
                    disabled
                    className="w-4 h-4 rounded text-brand-blue focus:ring-brand-blue cursor-not-allowed"
                  />
                  <span className="text-sm font-medium text-slate-700 select-none">Request Owner Approval</span>
                </label>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold"
              >
                <Edit2 size={13} className="text-slate-500" />
                <span>Edit</span>
              </Button>
              <Button
                variant="primary"
                size="sm"
                title="Submit quotation"
                onClick={handleSubmitClick}
                disabled={isSubmitting}
                className="flex items-center gap-1.5 px-3.5 py-1 text-xs font-semibold"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={13} className="animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={13} />
                    <span>Submit</span>
                  </>
                )}
              </Button>
              <div className="w-px h-6 bg-slate-300 mx-1"></div>
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="p-1.5 rounded hover:bg-red-50 hover:text-red-600 text-slate-500 transition-colors disabled:opacity-50"
                title="Close Preview"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Scrollable Document Container */}
          <div className="flex-1 overflow-auto py-6 px-4 sm:px-8 bg-slate-100 flex justify-center w-full">
            <div className="bg-white shadow-md ring-1 ring-slate-200 w-full max-w-[960px]" ref={pdfRef}>
              <RoomQuotationTemplate data={{ ...data, referenceDesigner: rawDesigner, designer: rawDesigner, rows: data?.rows?.filter(r => !r.isStock && !r._fromStock) || [] }} />
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal — mounted on top */}
      {showPayment && (
        <PaymentModal
          quotationNo={data?.quotationNo}
          grandTotal={data?.totals?.grandTotal ? Number(String(data.totals.grandTotal).replace(/[^0-9.]/g, '')) : undefined}
          onClose={() => setShowPayment(false)}
          onConfirm={handlePaymentConfirm}
        />
      )}
    </>
  );
};
